import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A detailed medieval castle with towers and walls",
 *   "face_count": 100000,
 *   "generate_type": "Normal",
 *   "polygon_type": "triangle",
 *   "enable_pbr": true
 * }
 * ```
 */
export interface TencentHunyuan3dProParams {
  /**
   * 文本提示词，可选参数（与image_base64/image_url二选一，不能同时使用）。用于描述要生成的3D模型。长度不超过1024个UTF-8字符
   * @length 0 - 1024
   */
  prompt?: string;
  /**
   * Base64编码的图片，可选参数（与prompt二选一，不能同时使用）。图片格式支持jpg、png、jpeg、webp。图片大小不超过8MB，分辨率在128-5000px之间
   */
  image_base64?: string;
  /**
   * 图片URL地址，可选参数（与prompt二选一，不能同时使用）。图片格式支持jpg、png、jpeg、webp。图片大小不超过8MB，分辨率在128-5000px之间
   */
  image_url?: string;
  /**
   * 模型面数，可选参数。取值范围：40000-1500000。默认值根据模型复杂度自动确定
   * @range 40000 - 1500000
   */
  face_count?: number;
  /**
   * 生成类型，可选参数。控制生成的3D模型风格
   * @values Normal | LowPoly | Geometry | Sketch
   */
  generate_type?: 'Normal' | 'LowPoly' | 'Geometry' | 'Sketch';
  /**
   * 多边形类型，可选参数。控制模型的多边形结构
   * @values triangle | quadrilateral
   */
  polygon_type?: 'triangle' | 'quadrilateral';
  /**
   * 是否启用PBR材质，可选参数。启用后会生成更真实的材质效果（基于物理的渲染）
   */
  enable_pbr?: boolean;
  /**
   * 多视图图片列表，可选参数。用于提供物体的多个角度视图以提升生成质量
   */
  multi_view_images?: string[];
}

export interface TencentHunyuan3dProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * tencent_hunyuan_3d_pro
 *
 * 腾讯混元3D Pro模型生成接口，支持通过文本描述或图片生成高质量3D模型。
 * 
 * Model: Hunyuan-3D-Pro
 * Features:
 * - 文本到3D生成（T2-3D）
 * - 图像到3D生成（I2-3D）
 * - 多种模型格式输出（OBJ、GLB、STL、USDZ、FBX）
 * - 支持预览视频生成（MP4）
 * - 支持PBR材质
 * - 支持低多边形、几何体、草图等多种风格
 *
 * @example
 * ```typescript
 * import { initSeacloud, tencentHunyuan3dPro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await tencentHunyuan3dPro(
 *   {
 *     "prompt": "A detailed medieval castle with towers and walls",
 *     "face_count": 100000,
 *     "generate_type": "Normal",
 *     "polygon_type": "triangle",
 *     "enable_pbr": true
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 TencentHunyuan3dProParams 接口定义
 * @returns 生成的资源列表
 */
export async function tencentHunyuan3dPro(
  params: TencentHunyuan3dProParams,
): Promise<TencentHunyuan3dProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 4000,
    maxAttempts: 90,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'tencent_hunyuan_3d_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: TencentHunyuan3dProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
